import API from '../../../api/APIUrl'
import { API_BASE_URL } from '../../../utils/contants'
import { setupAxios } from '../../modules/auth/core/AuthHelpers'

interface EncashmentCalculationRequest {
  perDayRate?: number
  calculationDate?: string
}

interface EncashmentCalculation {
  totalLeaveBalance: number
  encashmentBalance: number
  isEligible: boolean
  details: {
    financialYear: string
    openingBalance: number
    totalConsumed: number
    minimumCarryOver: number
    baseEntitlement: number
    previousYearCarry: number
    calculationDate: string
    eligibilityStatus: string
  }
}

interface EncashmentRecord {
  id: string
  userId: string
  financialYear: string
  calculationDate: string
  totalLeaveBalance: number
  openingBalance: number
  totalConsumed: number
  minimumCarryOver: number
  encashmentBalance: number
  isEligibleForEncashment: boolean
  payrollProcessed: boolean
  payrollProcessedDate?: string
  payrollAmount?: number
  perDayRate?: number
  status: string
  remarks?: string
  calculatedBy?: string
  createdAt: string
  updatedAt: string
  user: {
    firstName: string
    lastName: string
    employeeCode: string
    email: string
  }
}

interface EncashmentSummaryData {
  summary: {
    financialYear: string
    totalUsers: number
    eligibleUsers: number
    notEligibleUsers: number
    totalEncashmentDays: number
    totalPayrollAmount: number
    averageEncashmentPerUser: number
  }
  eligibleUsers: EncashmentRecord[]
  totalEncashmentDays: number
  totalPayrollAmount: number
}

// Standard API Response format following backend pattern
interface APIResponse<T = any> {
  status: 'success' | 'error' | 'not_found'
  message: string
  data?: T
  error?: string
}

export class EarnedLeaveEncashmentService {
  private static baseUrl = `${API_BASE_URL}/${API.LEAVEBALANCE}/encashment`

  // Helper method to get authentication headers
  private static getAuthHeaders(): HeadersInit {
    const { token } = setupAxios()
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  }

  // Calculate earned leave encashment for a user
  static async calculateEncashment(userId: string): Promise<APIResponse<{
    userId: string
    encashment: EncashmentCalculation
    message: string
    businessRule: string
  }>> {
    try {
      const response = await fetch(`${this.baseUrl}/calculate/${userId}`, {
        headers: this.getAuthHeaders()
      })
      return await response.json()
    } catch (error) {
      console.error('Error calculating encashment:', error)
      throw new Error('Failed to calculate encashment')
    }
  }

  // Create or update encashment record for a user  
  static async createEncashmentRecord(
    userId: string,
    perDayRate?: number
  ): Promise<APIResponse<{
    userId: string
    encashmentRecord: EncashmentRecord
    message: string
    payrollReady: boolean
  }>> {
    try {
      const url = perDayRate 
        ? `${this.baseUrl}/create/${userId}?perDayRate=${perDayRate}`
        : `${this.baseUrl}/create/${userId}`
      
      const { token } = setupAxios()
      const response = await fetch(url, { 
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({}) // Send empty JSON object
      })
      return await response.json()
    } catch (error) {
      console.error('Error creating encashment record:', error)
      throw new Error('Failed to create encashment record')
    }
  }

  // Get encashment record for a user
  static async getEncashmentRecord(
    userId: string,
    financialYear?: string
  ): Promise<APIResponse<{
    userId: string
    encashmentRecord: EncashmentRecord
    message: string
  }>> {
    try {
      const url = financialYear 
        ? `${this.baseUrl}/record/${userId}?financialYear=${financialYear}`
        : `${this.baseUrl}/record/${userId}`
      
      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      })
      return await response.json()
    } catch (error) {
      console.error('Error fetching encashment record:', error)
      throw new Error('Failed to fetch encashment record')
    }
  }

  // Get encashment summary for all users
  static async getEncashmentSummary(
    financialYear?: string
  ): Promise<APIResponse<{
    summary: EncashmentSummaryData
    message: string
    payrollSummary: {
      totalDays: number
      totalAmount: number
      averageDaysPerUser: number
    }
  }>> {
    try {
      const url = financialYear 
        ? `${this.baseUrl}/summary?financialYear=${financialYear}`
        : `${this.baseUrl}/summary`
      
      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      })
      return await response.json()
    } catch (error) {
      console.error('Error fetching encashment summary:', error)
      throw new Error('Failed to fetch encashment summary')
    }
  }

  // Mark encashment as processed for payroll
  static async markAsPayrollProcessed(
    userId: string,
    payrollAmount?: number
  ): Promise<APIResponse<{
    userId: string
    processedRecord: EncashmentRecord
    message: string
  }>> {
    try {
      const url = payrollAmount 
        ? `${this.baseUrl}/process/${userId}?payrollAmount=${payrollAmount}`
        : `${this.baseUrl}/process/${userId}`
      
      const response = await fetch(url, { 
        method: 'POST',
        headers: this.getAuthHeaders()
      })
      return await response.json()
    } catch (error) {
      console.error('Error marking as processed:', error)
      throw new Error('Failed to mark as processed')
    }
  }

  // Calculate encashment for all eligible users (Batch processing)
  static async calculateForAllUsers(
    perDayRate?: number
  ): Promise<APIResponse<{
    batchResult: {
      processed: number
      eligible: number
      notEligible: number
      errors: any[]
    }
    message: string
  }>> {
    try {
      const url = perDayRate 
        ? `${this.baseUrl}/calculate-all?perDayRate=${perDayRate}`
        : `${this.baseUrl}/calculate-all`
      
      const response = await fetch(url, { 
        method: 'POST',
        headers: this.getAuthHeaders()
      })
      return await response.json()
    } catch (error) {
      console.error('Error calculating for all users:', error)
      throw new Error('Failed to calculate for all users')
    }
  }

  // Get encashment history for a user
  static async getEncashmentHistory(
    userId: string,
    limit?: number
  ): Promise<APIResponse<{
    userId: string
    history: EncashmentRecord[]
    message: string
  }>> {
    try {
      const url = limit 
        ? `${this.baseUrl}/history/${userId}?limit=${limit}`
        : `${this.baseUrl}/history/${userId}`
      
      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      })
      return await response.json()
    } catch (error) {
      console.error('Error fetching encashment history:', error)
      throw new Error('Failed to fetch encashment history')
    }
  }
}

export default EarnedLeaveEncashmentService
